import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import linkService from '../services/link.service';
import { LinkUpdateRequestDto } from '../dto/request/link-update-request.dto';
import { LinkSaveRequestDto } from '../dto/request/link-save-request.dto';
import { LinkResponseDto } from '../dto/response/link-response.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  links: [] as LinkResponseDto[] | null,
  linkStatus: 'idle',
  link: {} as LinkResponseDto | null,
  error: null as string | null,
};

export const fetchLinkById = createAsyncThunk(
  'links/fetchLinkById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await linkService.fetchLinkById(params.id, params.language);
  }
);

// export const fetchManagementLinkById = createAsyncThunk(
//   'links/fetchManagementLinkById',
//   async (id: string) => {
//     return await linkService.fetchManagementLinkById(id);
//   }
// );

export const fetchLinksByCompactIndicatorId = createAsyncThunk(
  'links/fetchLinksByCompactIndicatorId',
  async (compactIndicatorId: string) => {
    return await linkService.fetchLinksByCompactIndicatorId(compactIndicatorId);
  }
);

export const saveLink = createAsyncThunk(
  'links/saveLink',
  async (saveRequest: LinkSaveRequestDto) => {
    return await linkService.saveLink(saveRequest);
  }
);

export const updateLink = createAsyncThunk(
  'links/updateLink',
  async (params: {
    updateRequest: LinkUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await linkService.updateLink(params.updateRequest, params.language);
  }
);

export const deleteLink = createAsyncThunk(
  'links/deleteLink',
  async (
    {
      existingId,
      compactId,
      landscapeIndicatorId,
    }: { existingId: string; compactId: string; landscapeIndicatorId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await linkService.deleteLink(existingId, compactId);
      dispatch(fetchLinksByCompactIndicatorId(landscapeIndicatorId));
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const linkSlice = createSlice({
  name: 'linkSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLinkById.pending, (state) => {
        state.linkStatus = 'loading';
      })
      .addCase(fetchLinkById.fulfilled, (state, action) => {
        state.linkStatus = 'succeeded';
        state.link = action.payload;
      })
      .addCase(fetchLinkById.rejected, (state, action) => {
        state.linkStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      // .addCase(fetchManagementLinkById.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchManagementLinkById.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.link = action.payload;
      // })
      // .addCase(fetchManagementLinkById.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(fetchLinksByCompactIndicatorId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLinksByCompactIndicatorId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.links = action.payload;
      })
      .addCase(fetchLinksByCompactIndicatorId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveLink.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.links?.push(action.payload);
        showSuccess('External resource added successfully.');
      })
      .addCase(saveLink.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateLink.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.links) {
          const linkIndex = state.links.findIndex(
            (link) => link.id === action.payload.id
          );
          if (linkIndex !== -1) {
            state.links[linkIndex] = action.payload;
          }
        }
      })
      .addCase(updateLink.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteLink.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteLink.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Resource deleted successfully.');
      })
      .addCase(deleteLink.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Deleting resource failed. ${action.payload as string}`);
      });
  },
});

export default linkSlice.reducer;
